import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../Icon';
import { searchSowOpportunities } from '../../lib/api';
import { fmt } from '../../lib/format';
import type { SowOpportunityHit } from '../../types';

interface Props {
  onSelect: (hit: SowOpportunityHit) => void;
  autoFocus?: boolean;
  onQueryChange?: () => void;
  width?: number;
}

/**
 * Salesforce opportunity typeahead for the toolbar: search icon inside a
 * bordered `.input`, results in a fixed-position panel anchored to the field
 * (fixed = immune to ancestor overflow clipping, which cut the panel off when
 * it was absolutely positioned inside the toolbar). Debounced 300ms; matches
 * O-number, opportunity name, and account name.
 */
export function OppSearchBox({ onSelect, onQueryChange, autoFocus, width = 360 }: Props): JSX.Element {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Track the input's viewport position while the panel is open so the
  // fixed-position panel stays glued to it through resize/scroll.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setAnchor({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 420) });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  const { data: hits, isFetching } = useQuery({
    queryKey: ['sow-opp-search', debounced],
    queryFn: () => searchSowOpportunities(debounced),
    enabled: debounced.length >= 2,
  });

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Icon
        name="search"
        size={14}
        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--mute)', pointerEvents: 'none' }}
      />
      <input
        ref={inputRef}
        className="input"
        style={{ width, paddingLeft: 32, border: '1px solid var(--border-1)' }}
        autoFocus={autoFocus}
        placeholder="Search opportunities…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          onQueryChange?.();
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && debounced.length >= 2 && anchor && (
        <div
          style={{
            position: 'fixed',
            top: anchor.top,
            left: anchor.left,
            width: anchor.width,
            zIndex: 60,
            background: '#fff',
            border: '1px solid var(--border-2)',
            boxShadow: '0 12px 32px rgba(7,29,73,0.14)',
            maxHeight: 420,
            overflowY: 'auto',
          }}
        >
          {isFetching && (
            <div className="d-meta" style={{ padding: '10px 14px' }}>Searching Salesforce…</div>
          )}
          {!isFetching && (hits ?? []).length === 0 && (
            <div className="d-meta" style={{ padding: '10px 14px' }}>No opportunities match “{debounced}”.</div>
          )}
          {(hits ?? []).map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => {
                setOpen(false);
                setQ('');
                onSelect(h);
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--paper)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                background: 'transparent',
                border: 0,
                borderBottom: '1px solid var(--border-1)',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              <span className="d-mono" style={{ fontSize: 12, color: 'var(--navy)', flex: '0 0 58px' }}>
                {h.number ?? '——'}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {h.accountName ?? '—'}
                </span>
                <span className="d-meta" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {h.name}
                </span>
              </span>
              <span style={{ flex: '0 0 auto', textAlign: 'right' }}>
                <span className="d-tnum" style={{ display: 'block', fontSize: 12.5, color: 'var(--ink)' }}>
                  {h.amount != null ? fmt.cur(h.amount) : ''}
                </span>
                <span className="d-meta" style={{ display: 'block', fontSize: 11 }}>{h.stageName ?? ''}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
